import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/api';

async function pollOAuthToken(provider, state) {
  const maxAttempts = 40;
  for (let i = 0; i < maxAttempts; i++) {
    const res = await authFetch(`/api/oauth/${provider}/poll?state=${encodeURIComponent(state)}`);
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Failed to retrieve token');
    }
    const result = await res.json();
    if (result.ready && result.token) return result.token;
    await new Promise(r => setTimeout(r, 500));
  }
  throw new Error('Timed out waiting for authentication. Please try again.');
}

export function useOAuthConnect(onSuccess, onError) {
  const [connecting, setConnecting] = useState(null);

  const processOAuthResult = useCallback(async (data) => {
    if (data.status === 'error') {
      onError?.(`OAuth failed: ${data.payload}`);
      setConnecting(null);
      return;
    }
    const { provider, payload: state } = data;
    if (!provider || !state) {
      onError?.('Invalid OAuth response');
      setConnecting(null);
      return;
    }
    try {
      const token = await pollOAuthToken(provider, state);
      onSuccess?.(provider, token);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setConnecting(null);
    }
  }, [onSuccess, onError]);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'oauth_callback') processOAuthResult(e.data);
    };
    const handleStorage = (e) => {
      if (e.key !== 'oauth_result') return;
      try {
        const data = JSON.parse(e.newValue);
        if (data?.type === 'oauth_callback') {
          localStorage.removeItem('oauth_result');
          processOAuthResult(data);
        }
      } catch (_) {}
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [processOAuthResult]);

  const startOAuth = async (provider) => {
    setConnecting(provider);
    try {
      const res = await authFetch(`/api/oauth/${provider}/authorize`);
      if (!res.ok) throw new Error('Failed to get auth URL');
      const { url } = await res.json();
      window.open(url, `oauth_${provider}_${Date.now()}`, 'width=600,height=700,left=300,top=100');
    } catch (e) {
      onError?.(e.message);
      setConnecting(null);
    }
  };

  return { startOAuth, connecting };
}
